import { useState, useRef } from "react";
import { X, UploadCloud, Loader2, Sparkles, AlertCircle, CheckCircle2, Plus, Trash2 } from "lucide-react";
import axios from "axios";

const blank = () => ({
  campaign_name: "", impressions: 0, events: 0, event_rate: 0,
  weekly: [], event_types: [], click_conversions_total: 0, click_conversions: [],
  view_through_total: 0, view_through_conversions: [], creative_assets_total: 0,
  creative_types: [], extensions: [], extension_types: [], search_themes: [],
});

const inputCls = "w-full px-2 py-1 text-sm border border-slate-300 rounded-md focus:border-lime-500 focus:ring-1 focus:ring-lime-500";

export const Field = ({ label, value, onChange, type = "number", testId }) => (
  <div>
    <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 block mb-0.5">{label}</label>
    <input
      type={type}
      step="any"
      value={value ?? ""}
      onChange={(e) => onChange(type === "number" ? Number(e.target.value || 0) : e.target.value)}
      className={inputCls}
      data-testid={testId}
    />
  </div>
);

// Generic editable table. cols: [{key, label, type: "text"|"number"}]
export function RowsEditor({ title, cols, rows, onChange, testIdPrefix }) {
  const update = (i, key, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [key]: v } : r)));
  const add = () => onChange([...rows, Object.fromEntries(cols.map((c) => [c.key, c.type === "number" ? 0 : ""]))]);
  return (
    <div className="bg-white rounded-xl border-2 border-lime-200 p-4" data-testid={`${testIdPrefix}-editor`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-bold text-slate-900">{title}</p>
        <button onClick={add} className="text-xs text-lime-700 font-semibold inline-flex items-center gap-1" data-testid={`${testIdPrefix}-add`}>
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
      {rows.length === 0 && <p className="text-xs text-slate-400 italic">Nothing extracted. Add rows if needed.</p>}
      {rows.length > 0 && (
        <div className="flex items-center gap-2 mb-1 pr-9">
          {cols.map((c) => (
            <span key={c.key} className={`text-[10px] font-semibold uppercase tracking-wide text-slate-500 ${c.type === "number" ? "w-20 flex-none" : "flex-1 min-w-0"}`}>{c.label}</span>
          ))}
        </div>
      )}
      <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            {cols.map((c) => (
              <input
                key={c.key}
                type={c.type}
                step="any"
                placeholder={c.label}
                title={c.label}
                value={r[c.key] ?? ""}
                onChange={(e) => update(i, c.key, c.type === "number" ? Number(e.target.value || 0) : e.target.value)}
                className={`${inputCls.replace("w-full ", "")} ${c.type === "number" ? "w-20 flex-none tabular-nums" : "flex-1 min-w-0"}`}
                data-testid={`${testIdPrefix}-${c.key}-${i}`}
              />
            ))}
            <button onClick={() => onChange(rows.filter((_, j) => j !== i))} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-500" data-testid={`${testIdPrefix}-remove-${i}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const T = (key, label) => ({ key, label, type: "text" });
const N = (key, label) => ({ key, label, type: "number" });

function ConfirmForm({ data, set }) {
  const f = (k) => (v) => set({ ...data, [k]: v });
  // Raw textarea text so commas/spaces survive while typing; parsed list lives in data.search_themes.
  const [themesText, setThemesText] = useState(null);
  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl border-2 border-lime-200 p-4">
        <p className="text-sm font-bold text-slate-900 mb-3">Headline numbers</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="col-span-2 md:col-span-4"><Field label="Campaign / Creative Group" type="text" value={data.campaign_name} onChange={f("campaign_name")} testId="pmax-campaign-name" /></div>
          <Field label="Impressions" value={data.impressions} onChange={f("impressions")} testId="pmax-impressions" />
          <Field label="Events" value={data.events} onChange={f("events")} testId="pmax-events" />
          <Field label="Event Rate %" value={data.event_rate} onChange={f("event_rate")} testId="pmax-event-rate" />
          <Field label="Creative Assets" value={data.creative_assets_total} onChange={f("creative_assets_total")} testId="pmax-creative-assets-total" />
          <Field label="Click Conversions" value={data.click_conversions_total} onChange={f("click_conversions_total")} testId="pmax-click-conv-total" />
          <Field label="View-through Conversions" value={data.view_through_total} onChange={f("view_through_total")} testId="pmax-view-conv-total" />
        </div>
        <p className="text-[11px] text-slate-500 mt-2">Ad spend and cost per event are never saved, so clients never see them.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <RowsEditor title="Week by Week Events" cols={[T("week", "Week"), N("events", "Events")]} rows={data.weekly} onChange={f("weekly")} testIdPrefix="pmax-weekly" />
        <RowsEditor title="Event Breakdown by Type (%)" cols={[T("type", "Type"), N("pct", "%")]} rows={data.event_types} onChange={f("event_types")} testIdPrefix="pmax-event-types" />
        <RowsEditor title="Click Conversions" cols={[T("action", "Action"), N("count", "Count")]} rows={data.click_conversions} onChange={f("click_conversions")} testIdPrefix="pmax-click-conv" />
        <RowsEditor title="View-through Conversions" cols={[T("action", "Action"), N("count", "Count")]} rows={data.view_through_conversions} onChange={f("view_through_conversions")} testIdPrefix="pmax-view-conv" />
        <RowsEditor title="Creative Types" cols={[T("type", "Type"), N("count", "Count")]} rows={data.creative_types} onChange={f("creative_types")} testIdPrefix="pmax-creative-types" />
        <RowsEditor title="Extension Type Event Rate (%)" cols={[T("type", "Type"), N("event_rate", "%")]} rows={data.extension_types} onChange={f("extension_types")} testIdPrefix="pmax-ext-types" />
      </div>
      <RowsEditor
        title="Top Extensions"
        cols={[T("text", "Extension text"), T("type", "Type"), N("impressions", "Impr."), N("events", "Events"), N("event_rate", "Rate %")]}
        rows={data.extensions}
        onChange={f("extensions")}
        testIdPrefix="pmax-extensions"
      />
      <div className="bg-white rounded-xl border-2 border-lime-200 p-4">
        <p className="text-sm font-bold text-slate-900 mb-2">Search Themes ({data.search_themes.length})</p>
        <textarea
          rows={4}
          value={themesText ?? data.search_themes.join(", ")}
          onChange={(e) => {
            setThemesText(e.target.value);
            f("search_themes")(e.target.value.split(",").map((s) => s.trim()).filter(Boolean));
          }}
          className={inputCls}
          placeholder="Comma separated"
          data-testid="pmax-search-themes-input"
        />
      </div>
    </div>
  );
}

export default function PmaxImporter({ open, onClose, clients, API, onSaved }) {
  const fileRef = useRef(null);
  const [step, setStep] = useState("upload");
  const [clientId, setClientId] = useState("");
  const [reportStart, setReportStart] = useState("");
  const [reportEnd, setReportEnd] = useState("");
  const [file, setFile] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [data, setData] = useState(blank());
  const [periodLabel, setPeriodLabel] = useState("");
  const singleClients = clients.filter((c) => c.client_type !== "multi");

  const reset = () => {
    setStep("upload"); setClientId(""); setReportStart(""); setReportEnd("");
    setFile(null); setErrorMsg(""); setData(blank()); setPeriodLabel("");
  };
  const close = () => { reset(); onClose(); };

  const runExtract = async () => {
    setErrorMsg("");
    setStep("extracting");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/import/pmax/extract?client_id=${clientId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 180000,
      });
      const d = res.data || {};
      setData(Object.fromEntries(Object.entries(blank()).map(([k, v]) => [k, d[k] ?? v])));
      setPeriodLabel(d.report_period_label || "");
      setStep("confirm");
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || e.message || "Extraction failed");
      setStep("upload");
    }
  };

  const runSave = async () => {
    if (!data.impressions && !data.events) return setErrorMsg("Impressions and Events are both zero. Please review before saving.");
    setErrorMsg("");
    setStep("saving");
    try {
      await axios.post(`${API}/import/pmax/save`, { ...data, client_id: clientId, report_start: reportStart, report_end: reportEnd }, { timeout: 30000 });
      setStep("done");
      onSaved && onSaved();
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || e.message || "Save failed");
      setStep("confirm");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="pmax-importer-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-lime-400 to-cyan-500 flex items-center justify-center text-white">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <p className="text-lg font-bold text-slate-900">PMAX (Performance Max)</p>
              <p className="text-xs text-slate-500">Drop the Performance Max Campaign Reporting PDF. Single-location clients only.</p>
            </div>
          </div>
          <button onClick={close} className="p-2 rounded-lg hover:bg-slate-100" data-testid="pmax-close"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        <div className="p-5">
          {errorMsg && (
            <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm" data-testid="pmax-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{errorMsg}</span>
            </div>
          )}

          {step === "upload" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">Client</label>
                  <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="pmax-client-select">
                    <option value="">Select client…</option>
                    {singleClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">Report Start</label>
                  <input type="date" value={reportStart} onChange={(e) => setReportStart(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="pmax-report-start" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">Report End</label>
                  <input type="date" value={reportEnd} onChange={(e) => setReportEnd(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="pmax-report-end" />
                </div>
              </div>
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); setFile(e.dataTransfer.files?.[0] || null); }}
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-lime-500 hover:bg-lime-50/40 transition-colors cursor-pointer"
                data-testid="pmax-drop-zone"
              >
                <UploadCloud className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                <p className="text-sm font-medium text-slate-700">{file ? `${file.name} ready` : "Drop the PMAX PDF here, or click to browse"}</p>
                <p className="text-xs text-slate-500 mt-1">AI reads the charts too. Takes about 15 to 60 seconds.</p>
                <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="pmax-file-input" />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={close} className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="pmax-cancel">Cancel</button>
                <button
                  onClick={runExtract}
                  disabled={!clientId || !reportStart || !reportEnd || !file}
                  className="px-5 py-2 text-sm rounded-lg bg-gradient-to-br from-lime-500 to-cyan-600 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 flex items-center gap-2"
                  data-testid="pmax-extract-btn"
                >
                  <Sparkles className="w-4 h-4" /> Extract from PDF
                </button>
              </div>
            </div>
          )}

          {(step === "extracting" || step === "saving") && (
            <div className="py-16 text-center" data-testid={`pmax-${step}`}>
              <Loader2 className="w-10 h-10 mx-auto text-lime-600 animate-spin mb-3" />
              <p className="text-sm font-medium text-slate-700">{step === "extracting" ? "Reading your PMAX report…" : "Saving PMAX report…"}</p>
            </div>
          )}

          {step === "confirm" && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-lime-50 border border-lime-200 text-lime-900 text-xs">
                <p className="font-semibold mb-0.5">Review extracted values</p>
                <p>{periodLabel ? `Period in PDF: ${periodLabel}. ` : ""}Chart numbers are read by AI, so double check them against the PDF.</p>
              </div>
              <ConfirmForm data={data} set={setData} />
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setStep("upload")} className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="pmax-back">Back</button>
                <button onClick={runSave} className="px-5 py-2 text-sm rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 flex items-center gap-2" data-testid="pmax-save-btn">
                  <CheckCircle2 className="w-4 h-4" /> Looks Good, Save
                </button>
              </div>
            </div>
          )}

          {step === "done" && (
            <div className="py-12 text-center" data-testid="pmax-done">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3"><CheckCircle2 className="w-8 h-8 text-emerald-600" /></div>
              <p className="text-base font-bold text-slate-900 mb-1">Saved!</p>
              <p className="text-sm text-slate-600 mb-5">Open the client&apos;s report and look under Every Channel, Up Close.</p>
              <div className="flex justify-center gap-2">
                <button onClick={reset} className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="pmax-import-another">Import Another</button>
                <button onClick={close} className="px-5 py-2 text-sm rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700" data-testid="pmax-done-close">Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
